'use client'

import Link from 'next/link'
import { useLandingPageViewmodel } from '@/features/landing/landing-page.hooks'
import { LandingHomeLink } from '@/features/landing/view/LandingHomeLink'
import { LandingLeadForm } from '@/features/landing/view/LandingLeadForm'
import { routePaths } from '@/shared/config/site'
import { Button } from '@/shared/ui/Button'

export function EngineersLandingPage() {
  const landingPageViewmodel = useLandingPageViewmodel()
  const { content } = landingPageViewmodel.page

  return (
    <div className="page-shell bg-[#030307] text-phosphor-ivory">
      <main className="relative overflow-hidden" id={'content'}>
        <div className="absolute left-5 top-5 z-20 md:left-8 md:top-8">
          <LandingHomeLink className="hover:bg-[#15122e]" />
        </div>
        <section className="relative isolate overflow-hidden px-5 pb-8 pt-20 md:px-8 md:pt-20 lg:px-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_65%_26%,rgba(138,124,255,0.22),transparent_30%),linear-gradient(180deg,#080816_0%,#030307_80%)]" />
          <img
            alt={content.visual.alt}
            className="absolute inset-y-0 right-[-46rem] h-full max-w-none object-cover opacity-76 [clip-path:polygon(18%_0,100%_0,100%_100%,0_100%)] md:right-[-32rem] lg:right-[-14rem] xl:right-0"
            decoding="async"
            height={860}
            src={content.visual.src}
            width={1800}
          />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,#030307_0%,#030307_34%,rgba(3,3,7,0.45)_62%,#030307_100%)]" />
          <div className="absolute bottom-16 right-0 hidden h-px w-[62vw] bg-[#8a7cff]/42 lg:block" />
          <div className="absolute right-24 top-24 hidden h-[58vh] w-px bg-[#f7b955]/36 lg:block" />
          <div className="relative mx-auto flex max-w-7xl flex-col justify-start">
            <div className="z-10 max-w-5xl">
              <h1 className="max-w-5xl font-sans text-4xl font-black leading-none text-[#f4f0ff] md:text-6xl lg:text-7xl">
                {content.title}
              </h1>
              <p className="mt-7 max-w-3xl text-lg font-semibold leading-8 text-white/76 md:text-2xl md:leading-10">
                {content.intro}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild className="border-[#8a7cff] bg-[#8a7cff] text-[#030307] hover:bg-[#9b90ff]" size="lg">
                  <a href="#lead">{content.primaryCta}</a>
                </Button>
                <Button asChild className="border-white/12 bg-[#10101a] text-white hover:bg-[#18182a]" size="lg">
                  <Link href={routePaths.resume}>{content.secondaryCta}</Link>
                </Button>
              </div>
            </div>
            <div className="mt-12 flex max-w-5xl flex-wrap gap-3">
              {content.proofItems.map((item) => (
                <article key={item.title} className="max-w-xs bg-[#030307]/74 p-4">
                  <h2 className="font-mono text-sm font-semibold text-[#f7b955]">{item.title}</h2>
                  <p className="mt-3 text-sm leading-6 text-white/64">{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
        <section className="mx-auto flex max-w-7xl flex-col gap-4 px-5 pb-8 pt-4 md:px-8 lg:flex-row lg:items-stretch lg:px-10">
          <div className="bg-[#8a7cff] p-6 text-[#030307] md:p-8 lg:w-[22rem]">
            <h2 className="text-4xl font-black leading-none md:text-6xl">{content.processTitle}</h2>
          </div>
          <ol className="grid flex-1 gap-3">
            {content.processItems.map((item, index) => (
              <li
                key={item}
                className="grid gap-4 border-b border-[#8a7cff]/24 bg-[#080816] p-5 md:grid-cols-[4rem_minmax(0,1fr)]"
              >
                <span className="font-mono text-sm font-semibold text-[#8a7cff]">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <p className="text-base font-semibold leading-7 text-white/74">{item}</p>
              </li>
            ))}
          </ol>
        </section>
        <section className="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-10 md:px-8 lg:flex-row lg:items-start lg:px-10">
          <div className="lg:flex-1">
            <div className="flex flex-wrap gap-3">
              {content.proofItems.map((item) => (
                <article key={item.title} className="min-w-64 flex-1 bg-[#0c0c16] p-5">
                  <h2 className="font-mono text-base font-semibold text-[#f7b955]">{item.title}</h2>
                  <p className="mt-4 text-sm leading-7 text-white/68">{item.description}</p>
                </article>
              ))}
            </div>
            <div className="mt-5 flex flex-col gap-4 md:flex-row">
              <BoundaryList items={content.fitItems} title={content.fitTitle} />
              <BoundaryList danger items={content.boundaryItems} title={content.boundaryTitle} />
            </div>
          </div>
          <LandingLeadForm className="border-[#8a7cff]/34 bg-[#080816] lg:sticky lg:top-8 lg:w-[29rem]" />
        </section>
        <section className="mx-auto grid max-w-7xl gap-5 px-5 pb-16 md:px-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:px-10">
          <div>
            <h2 className="max-w-4xl text-4xl font-black leading-none text-[#f4f0ff] md:text-6xl">
              {content.closingTitle}
            </h2>
            <p className="mt-5 max-w-3xl text-base font-semibold leading-8 text-white/66">{content.closingBody}</p>
          </div>
          <Button asChild className="border-[#8a7cff] bg-[#8a7cff] text-[#030307] hover:bg-[#9b90ff]" size="lg">
            <a href="#lead">{content.primaryCta}</a>
          </Button>
        </section>
      </main>
    </div>
  )
}

function BoundaryList({ danger = false, items, title }: { danger?: boolean; items: string[]; title: string }) {
  return (
    <article className={danger ? 'flex-1 bg-[#18080d] p-5' : 'flex-1 border-l-4 border-[#8a7cff] bg-[#0c0c16] p-5'}>
      <h2 className={danger ? 'text-xl font-black text-[#ff3d5a]' : 'text-xl font-black text-[#f4f0ff]'}>{title}</h2>
      <ul className="mt-5 space-y-3">
        {items.map((item) => (
          <li key={item} className="text-sm leading-7 text-white/66">
            {item}
          </li>
        ))}
      </ul>
    </article>
  )
}
